import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import { Sword, Crown, Flame, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PremiumWelcome = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [showContent, setShowContent] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    // Check if user needs onboarding
    checkOnboardingStatus();
    
    // Animate content appearance - faster for mobile
    setTimeout(() => setShowContent(true), 300);
    setTimeout(() => setShowButton(true), 1000);
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/premium/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // If onboarding already completed, redirect to home
      if (response.data.onboarding_completed) {
        navigate("/hoy");
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
    }
  };

  const handleContinue = () => {
    navigate("/premium/onboarding");
  };

  return (
    <div 
      className="min-h-screen bg-[#0B0B0B] flex flex-col items-center px-6 py-8 overflow-y-auto"
      data-testid="premium-welcome"
    >
      {/* Background effects - positioned fixed but doesn't block interaction */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-[#D4AF37]/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-[#D4AF37]/5 to-transparent" />
      </div>

      {/* Spacer for vertical centering effect */}
      <div className="flex-1 min-h-[40px]" />

      {/* Animated sword icon */}
      <div className={cn(
        "transition-all duration-700 mb-6",
        showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}>
        <div className="relative">
          <div className="absolute inset-0 bg-[#D4AF37] blur-xl opacity-30 animate-pulse" />
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8B7500] flex items-center justify-center">
            <Sword className="w-10 h-10 sm:w-12 sm:h-12 text-[#0B0B0B]" />
          </div>
        </div>
      </div>

      {/* Title */}
      <h1 className={cn(
        "text-3xl sm:text-4xl font-heading font-black text-center mb-3 transition-all duration-700 delay-100",
        showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}>
        <span className="text-white">WELCOME TO</span>
        <br />
        <span className="text-[#D4AF37] tracking-wide">GOAT MODE</span>
      </h1>

      {/* Subtitle */}
      <p className={cn(
        "text-[#888] text-base sm:text-lg text-center max-w-sm mb-6 transition-all duration-700 delay-200",
        showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}>
        Has dado el paso que la mayoría nunca da.
      </p>

      {/* Main message */}
      <div className={cn(
        "text-center max-w-sm mb-6 transition-all duration-700 delay-300",
        showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      )}>
        <p className="text-white/90 text-base sm:text-lg leading-relaxed mb-3">
          Aquí empieza la verdadera transformación.
        </p>
        <p className="text-[#888] text-sm sm:text-base leading-relaxed">
          Física. Mental. Disciplina.
        </p>
        <p className="text-[#D4AF37] font-semibold mt-3 text-sm sm:text-base">
          Bienvenido al sistema GOAT.
        </p>
      </div>

      {/* Stats or badges */}
      <div className={cn(
        "flex items-center gap-4 sm:gap-6 mb-8 transition-all duration-700 delay-400",
        showContent ? "opacity-100" : "opacity-0"
      )}>
        <div className="flex flex-col items-center">
          <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37] mb-1" />
          <span className="text-[#666] text-[10px] sm:text-xs uppercase tracking-wider">Elite</span>
        </div>
        <div className="w-px h-6 sm:h-8 bg-[#2A2A2A]" />
        <div className="flex flex-col items-center">
          <Flame className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37] mb-1" />
          <span className="text-[#666] text-[10px] sm:text-xs uppercase tracking-wider">Disciplina</span>
        </div>
        <div className="w-px h-6 sm:h-8 bg-[#2A2A2A]" />
        <div className="flex flex-col items-center">
          <Sword className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37] mb-1" />
          <span className="text-[#666] text-[10px] sm:text-xs uppercase tracking-wider">Sistema</span>
        </div>
      </div>

      {/* CTA Button - Always visible */}
      <Button
        onClick={handleContinue}
        className={cn(
          "bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold py-5 px-6 sm:py-6 sm:px-8 text-base sm:text-lg rounded-xl transition-all duration-500 w-full max-w-xs",
          showButton ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
        )}
        data-testid="complete-profile-btn"
      >
        COMPLETAR MI PERFIL
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>

      {/* Spacer */}
      <div className="flex-1 min-h-[40px]" />

      {/* Footer note */}
      <p className={cn(
        "text-[#444] text-xs text-center pb-4 transition-all duration-700 delay-500",
        showButton ? "opacity-100" : "opacity-0"
      )}>
        Tu coach personal te espera
      </p>
    </div>
  );
};

export default PremiumWelcome;
